import {MyRequirementsMockup} from 'testUtils/dashboardMockups/MyRequirementsMockup';
import {useGetMyRequirementsQuery} from './myRequirements.generated';
import {KeywordStatus, WorkflowStatus} from 'types';
import {
	useGetWorkbenchBaselinesQuery,
	useGetWorkbenchItemsVexQuery,
} from './workbenchItems.generated';
import {
	WorkkbenchItemsVexMockupNew,
	WorkbenchItemsVexMockupAccepted,
	WorkbenchItemsVexMockupFinalized,
} from 'testUtils/dashboardMockups/WorkbenchItemsVexMockup';
import {
	GetMyRequirementsReviewQuery,
	useGetMyRequirementsReviewQuery,
} from './myRequirementsReview.generated';
import {MyRequirementsReviewMockup} from 'testUtils/dashboardMockups/MyRequirementsReviewMockup';
import {useGetActivityItemsQuery} from 'components/ActivityList/hooks/activityItems.generated';
import {useUserContext} from 'authentication/UserContext';
import {ActivityItemsMockup} from 'testUtils/dashboardMockups/ActivityItemsMockup';
import {
	VkoApproverDashboardItemType,
	VkoApproverItemsMockup,
} from 'testUtils/dashboardMockups/VkoApproverItems';
import {useGetVkoApproverWorkbenchRegulatoryDocumentsQuery} from './vkoApproverWorkbenchRegulatoryDocuments.generated';

/*
  This Hook is used for local test purposes only. Don't commit this flag set to true!
*/

const IS_DEBUG = false;

export const useDashboardQueries = () => {
	const {
		localAccountId,
		isAdmin,
		isVex,
		isVko,
		isApprover,
		isBedarfskoordinator,
	} = useUserContext();
	const {data: myReqData, loading: myReqLoading} = useGetMyRequirementsQuery({
		skip: !isAdmin && !isVex,
	});

	const {data: myRequirementsData, loading: myRequirementsLoading} =
		useGetMyRequirementsReviewQuery({skip: !isAdmin && !isApprover});

	const {data: workbenchItemsNew, loading: loadingWorkbenchItemsNew} =
		useGetWorkbenchItemsVexQuery({
			variables: {
				workflowStatus: [WorkflowStatus.Finalized],
				keywordStatus: KeywordStatus.New,
			},
			skip: !isAdmin && !isVex,
		});

	const {data: workbenchItemsAccepted, loading: loadingWorkbenchItemsAccepted} =
		useGetWorkbenchItemsVexQuery({
			variables: {
				workflowStatus: [WorkflowStatus.Finalized],
				keywordStatus: KeywordStatus.Accepted,
			},
			skip: !isAdmin && !isVex,
		});

	const {data: vkoApproverRegDocData, loading: vkoApproverRegDocLoading} =
		useGetVkoApproverWorkbenchRegulatoryDocumentsQuery({
			skip: !isAdmin && !(isVko && isApprover),
		});

	const {
		data: workbenchItemsFinalized,
		loading: loadingWorkbenchItemsFinalized,
	} = useGetWorkbenchItemsVexQuery({
		variables: {
			workflowStatus: [WorkflowStatus.Finalized],
			keywordStatus: KeywordStatus.Finalized,
		},
		skip: !isAdmin && !isVex,
	});

	const {data: activityItemsData, loading: loadingActivityItems} =
		useGetActivityItemsQuery({
			variables: {userId: localAccountId},
		});

	const {data: baselinesData, loading: loadingBaselines} =
		useGetWorkbenchBaselinesQuery({
			skip: !isBedarfskoordinator,
		});
	const getRequirementData = () => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: MyRequirementsMockup,
			};
		}
		return {
			data: myReqData,
			loading: myReqLoading,
		};
	};

	const getWorkbenchItemsVexQuery = (
		status:
			| KeywordStatus.Finalized
			| KeywordStatus.Accepted
			| KeywordStatus.New,
	) => {
		if (IS_DEBUG) {
			switch (status) {
				case KeywordStatus.Finalized:
					return {
						loading: false,
						data: WorkbenchItemsVexMockupFinalized,
					};
				case KeywordStatus.Accepted:
					return {
						loading: false,
						data: WorkbenchItemsVexMockupAccepted,
					};
				case KeywordStatus.New:
				default:
					return {
						loading: false,
						data: WorkkbenchItemsVexMockupNew,
					};
			}
		}
		switch (status) {
			case KeywordStatus.Finalized:
				return {
					loading: loadingWorkbenchItemsFinalized,
					data: workbenchItemsFinalized,
				};
			case KeywordStatus.Accepted:
				return {
					loading: loadingWorkbenchItemsAccepted,
					data: workbenchItemsAccepted,
				};
			case KeywordStatus.New:
			default:
				return {
					loading: loadingWorkbenchItemsNew,
					data: workbenchItemsNew,
				};
		}
	};

	const getMyRequirementsReviewQuery = (): {
		loading: boolean;
		data: GetMyRequirementsReviewQuery | undefined;
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: MyRequirementsReviewMockup,
			};
		}
		return {
			loading: myRequirementsLoading,
			data: myRequirementsData,
		};
	};

	const getActivityItems = () => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: ActivityItemsMockup,
			};
		}
		return {
			loading: loadingActivityItems,
			data: activityItemsData,
		};
	};

	const getBaseLineItems = () => {
		return {
			loading: loadingBaselines,
			data: baselinesData,
		};
	};

	const getVkoApproverTodoItems = (): {
		loading: boolean;
		data: VkoApproverDashboardItemType[];
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: VkoApproverItemsMockup.regulatoryDocuments
					.filter(item =>
						[
							WorkflowStatus.New,
							WorkflowStatus.InProgressInternal,
							WorkflowStatus.Modified,
						].includes(item.workflow.status),
					)
					.sort(
						(
							rd1: VkoApproverDashboardItemType,
							rd2: VkoApproverDashboardItemType,
						) => (rd1.createdAt > rd2.createdAt ? 1 : -1),
					),
			};
		}
		return {
			loading: vkoApproverRegDocLoading,
			data:
				vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
					?.todoItems || [],
		};
	};

	const getVkoApproverInputItems = (): {
		loading: boolean;
		data: VkoApproverDashboardItemType[];
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: VkoApproverItemsMockup.regulatoryDocuments
					.filter(item =>
						[
							WorkflowStatus.InProgressInternal,
							WorkflowStatus.Modified,
							WorkflowStatus.Finalized,
						].includes(item.workflow.status),
					)
					.sort(
						(
							rd1: VkoApproverDashboardItemType,
							rd2: VkoApproverDashboardItemType,
						) => (rd1.createdAt > rd2.createdAt ? 1 : -1),
					),
			};
		}
		return {
			loading: vkoApproverRegDocLoading,
			data:
				vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
					?.inputItems || [],
		};
	};

	const getVkoApproverMyScopeDocCount = (): number | undefined => {
		return vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
			?.myScopeDocCount;
	};

	return {
		myRequirementsReturnData: getRequirementData(),
		getWorkbenchItemsVexQuery,
		getMyRequirementsReviewQuery,
		getActivityItems,
		getBaseLineItems,
		getVkoApproverTodoItems,
		getVkoApproverInputItems,
		getVkoApproverMyScopeDocCount,
	};
};
